import { type GenericPolyElement, type PointBezierHandle } from '..';
import { type GenericShape } from '../../../lib/shapes';
import { type BoundsWithCenter } from '../../../utils/editor/bounds';
import { pointRotateRads, type Point } from '../../../utils/math';
import { pointsToShape } from '../../../utils/shapes';

export const getPolyShape = (
  element: GenericPolyElement,
  absolute: boolean = true,
): GenericShape => {
  const svgPath = pointsToShape(
    element.points,
    absolute ? element.x : 0,
    absolute ? element.y : 0,
    element.bezierHandles,
    {
      isClosed: !element.isOpen,
    },
  );
  return { data: svgPath, type: 'area' };
};

export const getBoundsShape = (
  bounds: BoundsWithCenter,
  elementX: number,
  elementY: number,
  angle: number,
): GenericShape => {
  const [minX, minY, maxX, maxY, cx, cy] = bounds;

  const points: Point[] = [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];

  const rotatedPoints = points.map((point) =>
    pointRotateRads(point, [cx, cy], angle),
  );

  const svgPath = pointsToShape(rotatedPoints, elementX, elementY, undefined, {
    isClosed: true,
  });

  return {
    data: svgPath,
    type: 'area',
  };
};

export const BEZIER_CIRCLE_COEFFICIENT = 0.55228;

export const getRectanglePoints = (
  width: number,
  height: number,
  scale: number = 1,
  centered: boolean = false,
): Point[] => {
  const w = width * scale;
  const h = height * scale;
  const offsetX = centered ? (width - w) / 2 : 0;
  const offsetY = centered ? (height - h) / 2 : 0;

  return [
    [offsetX, offsetY],
    [offsetX + w, offsetY],
    [offsetX + w, offsetY + h],
    [offsetX, offsetY + h],
  ];
};

export const getEllipsePoints = (
  width: number,
  height: number,
  scale: number = 1,
  centered: boolean = false,
): Point[] => {
  const w = width * scale;
  const h = height * scale;
  const offsetX = centered ? (width - w) / 2 : 0;
  const offsetY = centered ? (height - h) / 2 : 0;
  const xRadius = w / 2;
  const yRadius = h / 2;

  return [
    [offsetX + xRadius, offsetY],
    [offsetX + w, offsetY + yRadius],
    [offsetX + xRadius, offsetY + h],
    [offsetX, offsetY + yRadius],
  ];
};

export const getEllipseBezierHandles = (
  width: number,
  height: number,
  scale: number = 1,
  centered: boolean = false,
): PointBezierHandle[] => {
  const w = width * scale;
  const h = height * scale;
  const offsetX = centered ? (width - w) / 2 : 0;
  const offsetY = centered ? (height - h) / 2 : 0;
  const xRadius = w / 2;
  const yRadius = h / 2;

  return [
    {
      before: [
        offsetX + xRadius - xRadius * BEZIER_CIRCLE_COEFFICIENT,
        offsetY,
      ],
      after: [offsetX + xRadius + xRadius * BEZIER_CIRCLE_COEFFICIENT, offsetY],
    },
    {
      before: [
        offsetX + w,
        offsetY + yRadius - yRadius * BEZIER_CIRCLE_COEFFICIENT,
      ],
      after: [
        offsetX + w,
        offsetY + yRadius + yRadius * BEZIER_CIRCLE_COEFFICIENT,
      ],
    },
    {
      before: [
        offsetX + xRadius + xRadius * BEZIER_CIRCLE_COEFFICIENT,
        offsetY + h,
      ],
      after: [
        offsetX + xRadius - xRadius * BEZIER_CIRCLE_COEFFICIENT,
        offsetY + h,
      ],
    },
    {
      before: [
        offsetX,
        offsetY + yRadius + yRadius * BEZIER_CIRCLE_COEFFICIENT,
      ],
      after: [offsetX, offsetY + yRadius - yRadius * BEZIER_CIRCLE_COEFFICIENT],
    },
  ];
};
